import { useEffect, useRef } from 'react';
import { styled } from 'goober';

import { useAccountSwitchNotificationFromUrl } from '@/features/account/hooks/use-account-switch-notification-from-url';

import { companyIdState } from '@/features/companies/overview/company.state';
import { useIsSpotlightCompanyView } from '@/features/companies/overview/company-fetch-context';
import { CompanyOverviewWidgetsGrid } from '@/features/companies/overview/company-overview-widgets-grid';
import { CompanyFooter } from '@/features/companies/overview/footer';
import { CompanyHeader } from '@/features/companies/overview/header/header';
import { scrollMainLayoutTo } from '@/page-layout';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Stack } from '@/ui/line/line';
import { WithHtmlSavvyTitle } from '@/with-html-savvy-title';

import { useParams } from 'react-router-dom';
import { ActionPanel } from '@/features/action-panel/action-panel';
import { useActionPanel } from '@/features/action-panel/use-action-panel';
import { SectionsPane } from '@/features/companies/overview/sections-pane/sections-pane';
import { useCompany } from '@/features/companies/overview/use-company.query';
import { useActionPanelSearchParams } from '../action-panel/use-action-panel-search-params';
import { useSetAtom } from 'jotai';
import { NotificationsSidebar } from '../notifications/notifications-sidebar';

const COMPANY_MAIN_CONTENT_WIDTH = 1235;
const COMPANY_STACK_GAP = 40;

export const CompanyPage = () => {
  const { companyId } = useParams();
  const setCompanyId = useSetAtom(companyIdState);
  useAccountSwitchNotificationFromUrl();
  const { closeActionPanel, openActionPanelOnSearchParamTab } =
    useActionPanel();
  const { actionPanelSearchParam } = useActionPanelSearchParams();
  const { data: company, isFetched } = useCompany(companyId ?? null);
  const isSpotlightCompanyView = useIsSpotlightCompanyView();

  const openedDeepLinkKeyRef = useRef<string | null>(null);

  useEffect(() => {
    openedDeepLinkKeyRef.current = null;
  }, [companyId]);

  useEffect(() => {
    setCompanyId(companyId ?? null);

    return () => {
      setCompanyId(null);
      closeActionPanel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    scrollMainLayoutTo({ top: 0, behavior: 'instant' });
  }, [companyId]);

  useEffect(() => {
    if (isSpotlightCompanyView) {
      return;
    }

    if (!actionPanelSearchParam?.id || !isFetched) {
      return;
    }

    const key = `${companyId}:${actionPanelSearchParam.type}:${actionPanelSearchParam.id}`;

    if (openedDeepLinkKeyRef.current === key) {
      return;
    }

    openActionPanelOnSearchParamTab(actionPanelSearchParam, {
      context: 'company',
      title: company?.name.value ?? '',
    });
    openedDeepLinkKeyRef.current = key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actionPanelSearchParam,
    company?.name.value,
    companyId,
    isFetched,
    isSpotlightCompanyView,
  ]);

  return (
    <WithHtmlSavvyTitle
      title={company ? (company.name.value ?? '') : 'Company'}
    >
      <Flexbox name="x" justify="space-between">
        <CompanyPageContainer
          name="company-page-container"
          direction="column"
          fullWidth
        >
          <CompanyHeader />
          <Stack
            gap={`${COMPANY_STACK_GAP}px`}
            fullWidth
            maxWidth={`${COMPANY_MAIN_CONTENT_WIDTH}px`}
            margin="0 auto"
          >
            <CompanyOverviewWidgetsGrid />
            <CompanyFooter company={company} />
          </Stack>
        </CompanyPageContainer>
        {!isSpotlightCompanyView && <SectionsPane />}
        <ActionPanel />
        <NotificationsSidebar />
      </Flexbox>
    </WithHtmlSavvyTitle>
  );
};

const CompanyPageContainer = styled(Flexbox)`
  position: relative;
  overflow-x: hidden;
`;
